import { useSettingsUIStore } from '../../store/settingsUIStore';
import type { Prompt } from '../../types';
import { formatDate, getUserTimezone } from '../../utils/dateFormat';

// Icons
const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

interface PromptEditorProps {
  selectedPrompt: Prompt | null;
  isNewPromptMode: boolean;
  isLoading: boolean;
  selectedPromptId: string | null;
  editedName: string;
  editedSystemPrompt: string;
  editedUserPrompt: string;
  onNameChange: (name: string) => void;
  onSystemPromptChange: (prompt: string) => void;
  onUserPromptChange: (prompt: string) => void;
}

export function PromptEditor({
  selectedPrompt,
  isNewPromptMode,
  isLoading,
  selectedPromptId,
  editedName,
  editedSystemPrompt,
  editedUserPrompt,
  onNameChange,
  onSystemPromptChange,
  onUserPromptChange,
}: PromptEditorProps) {
  const {
    systemPromptExpanded,
    userPromptExpanded,
    setSystemPromptExpanded,
    setUserPromptExpanded,
  } = useSettingsUIStore();

  // Loading state
  if (isLoading && selectedPromptId && !isNewPromptMode) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
          <span className="inline-block w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
          Loading prompt...
        </div>
      </div>
    );
  }

  // No prompt selected
  if (!selectedPrompt && !isNewPromptMode) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400 dark:text-gray-500 italic">
        Select or create a prompt to begin editing
      </div>
    );
  }

  const userTimezone = getUserTimezone();

  return (
    <div className="flex flex-col h-full max-w-6xl mx-auto">
      {/* Prompt Name */}
      <div className="mb-4" data-onboarding="prompt-name-input">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Prompt Name</label>
        <input
          type="text"
          value={editedName}
          onChange={(e) => onNameChange(e.target.value)}
          className="w-full text-base font-semibold text-gray-800 dark:text-gray-100 px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-border focus:border-purple-400 focus:ring-2 focus:ring-purple-400/40 outline-none bg-white dark:bg-dark-recessed"
          placeholder="Enter prompt name..."
        />
        {selectedPrompt && !isNewPromptMode && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            <div>Created: {formatDate(selectedPrompt.created_at)} {userTimezone}</div>
            <div>Updated: {formatDate(selectedPrompt.updated_at)} {userTimezone}</div>
          </div>
        )}
      </div>

      {/* System Prompt Editor - Collapsible */}
      <div className="mb-3" data-onboarding="system-prompt-field">
        <button
          onClick={() => setSystemPromptExpanded(!systemPromptExpanded)}
          className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-recessed hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">System Prompt</label>
            <div className="group relative">
              <InfoIcon />
              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg">
                Defines AI's core identity, rules, tone, and long-term behavior. Like a constitution.
              </div>
            </div>
          </div>
          <ChevronIcon isOpen={systemPromptExpanded} />
        </button>
        {systemPromptExpanded && (
          <textarea
            key={isNewPromptMode ? 'new-system' : `system-${selectedPromptId}`}
            value={editedSystemPrompt}
            onChange={(e) => onSystemPromptChange(e.target.value)}
            className="w-full resize-none rounded-lg border border-gray-300 dark:border-dark-border focus:border-purple-400 focus:ring-2 focus:ring-purple-400/40 p-4 outline-none bg-white dark:bg-dark-recessed text-gray-800 dark:text-gray-200 font-mono text-sm leading-relaxed mt-2"
            placeholder="Enter system prompt..."
            style={{ minHeight: '70vh', height: '70vh' }}
          />
        )}
      </div>

      {/* User Prompt Editor - Collapsible */}
      <div className="mb-4" data-onboarding="user-prompt-field">
        <button
          onClick={() => setUserPromptExpanded(!userPromptExpanded)}
          className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-recessed hover:bg-gray-100 dark:hover:bg-dark-hover rounded-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">User Prompt</label>
            <div className="group relative">
              <InfoIcon />
              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg">
                Provides specific, dynamic task instructions for current interaction. Like a specific law for today's session.
              </div>
            </div>
          </div>
          <ChevronIcon isOpen={userPromptExpanded} />
        </button>
        {userPromptExpanded && (
          <textarea
            key={isNewPromptMode ? 'new-user' : `user-${selectedPromptId}`}
            value={editedUserPrompt}
            onChange={(e) => onUserPromptChange(e.target.value)}
            className="w-full resize-none rounded-lg border border-gray-300 dark:border-dark-border focus:border-purple-400 focus:ring-2 focus:ring-purple-400/40 p-4 outline-none bg-white dark:bg-dark-recessed text-gray-800 dark:text-gray-200 font-mono text-sm leading-relaxed mt-2"
            placeholder="Enter user prompt..."
            style={{ minHeight: '70vh', height: '70vh' }}
          />
        )}
      </div>
    </div>
  );
}
